import { ReactNode } from 'react';

import {
  Box,
  Chip,
  Divider,
  Card as MuiCard,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CNPJ_DIGITS_LENGTH,
  formatCNPJ,
  normalizeCNPJ,
} from 'src/utils/document';

import { useFundCarouselLayout } from '../FundCarouselContext';

interface FundCarouselCardRootProps {
  children: ReactNode;
}

interface FundCarouselBadgeProps {
  children: ReactNode;
}

interface FundCarouselTitleProps {
  children: string;
}

interface FundCarouselFieldProps {
  children: ReactNode;
}

interface FundCarouselFieldLabelProps {
  children: ReactNode;
}

interface FundCarouselFieldValueProps {
  children: ReactNode;
  color?: string;
  fontSize?: number;
  lineHeight?: number;
}

interface FundCarouselActionProps {
  children: ReactNode;
}

interface FundCarouselSectionProps {
  children: ReactNode;
}

function FundCarouselCardRoot({ children }: FundCarouselCardRootProps) {
  const layout = useFundCarouselLayout();
  const isAutoLayout = layout === 'auto';

  return (
    <MuiCard
      elevation={0}
      data-layout={layout}
      sx={{
        height: isAutoLayout ? 'auto' : '100%',
        minHeight: isAutoLayout ? 'unset' : 186,
        p: 1,
        borderRadius: 2,
        bgcolor: 'background.paper',
        boxShadow: (theme) =>
          `0 1px 3px ${alpha(theme.palette.common.black, 0.12)}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        boxSizing: 'border-box',
      }}>
      {children}
    </MuiCard>
  );
}

function FundCarouselHeader({ children }: FundCarouselSectionProps) {
  return (
    <Stack gap={0.5} flexShrink={0}>
      {children}
    </Stack>
  );
}

function FundCarouselDivider() {
  return <Divider sx={{ my: 0.15, flexShrink: 0 }} />;
}

function FundCarouselBody({ children }: FundCarouselSectionProps) {
  return (
    <Stack gap={0.75} sx={{ pt: 0.5, flexShrink: 0 }}>
      {children}
    </Stack>
  );
}

function FundCarouselBadge({ children }: FundCarouselBadgeProps) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={1}>
      <Chip
        label={children}
        size="small"
        variant="outlined"
        sx={{
          height: 18,
          borderRadius: 0.75,
          bgcolor: 'background.paper',
          borderColor: 'divider',
          color: 'text.secondary',
          fontWeight: 600,
          '& .MuiChip-label': {
            px: 0.6,
            fontSize: 9,
            lineHeight: 1,
          },
        }}
      />
    </Stack>
  );
}

function FundCarouselTitle({ children }: FundCarouselTitleProps) {
  return (
    <Tooltip title={children} arrow placement="top">
      <Typography
        variant="body2"
        color="text.primary"
        fontWeight={700}
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '100%',
          height: 28,
          maxHeight: 28,
          fontSize: 11.2,
          lineHeight: '14px',
          letterSpacing: 0,
          cursor: 'default',
        }}>
        {children}
      </Typography>
    </Tooltip>
  );
}

function FundCarouselFieldLabel({ children }: FundCarouselFieldLabelProps) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontSize: 11, lineHeight: 1.05, whiteSpace: 'nowrap' }}>
      {children}
    </Typography>
  );
}

function FundCarouselFieldValue({
  children,
  color = 'text.secondary',
  fontSize = 10.5,
  lineHeight = 1.15,
}: FundCarouselFieldValueProps) {
  return (
    <Typography
      variant="caption"
      color={color}
      fontWeight={700}
      sx={{
        fontSize,
        lineHeight,
        wordBreak: 'break-word',
      }}>
      {children}
    </Typography>
  );
}

function FundCarouselFieldRoot({ children }: FundCarouselFieldProps) {
  return <Stack gap={0.25}>{children}</Stack>;
}

const FundCarouselField = Object.assign(FundCarouselFieldRoot, {
  Label: FundCarouselFieldLabel,
  Value: FundCarouselFieldValue,
});

function FundCarouselAction({ children }: FundCarouselActionProps) {
  const layout = useFundCarouselLayout();
  const isAutoLayout = layout === 'auto';

  return (
    <Box
      sx={{
        mt: isAutoLayout ? 0.75 : 'auto',
        pt: isAutoLayout ? 0 : 0.5,
        width: '100%',
        px: 1,
        boxSizing: 'border-box',
        '& .MuiButton-root': {
          width: '100%',
          minWidth: 0,
          height: 22,
          minHeight: 22,
          py: 0,
          px: 0,
          fontSize: 11,
          fontWeight: 600,
          lineHeight: '22px',
          letterSpacing: 0,
          borderRadius: 1.25,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        },
      }}>
      {children}
    </Box>
  );
}

export function formatFundCnpj(cnpj: string) {
  const normalized = normalizeCNPJ(cnpj);

  return normalized.length === CNPJ_DIGITS_LENGTH
    ? formatCNPJ(normalized)
    : cnpj;
}

type FundCarouselCardComponent = ((
  props: FundCarouselCardRootProps,
) => JSX.Element) & {
  Header: typeof FundCarouselHeader;
  Badge: typeof FundCarouselBadge;
  Title: typeof FundCarouselTitle;
  Divider: typeof FundCarouselDivider;
  Body: typeof FundCarouselBody;
  Field: typeof FundCarouselField;
  Action: typeof FundCarouselAction;
};

export const FundCarouselCard = Object.assign(FundCarouselCardRoot, {
  Header: FundCarouselHeader,
  Badge: FundCarouselBadge,
  Title: FundCarouselTitle,
  Divider: FundCarouselDivider,
  Body: FundCarouselBody,
  Field: FundCarouselField,
  Action: FundCarouselAction,
}) as FundCarouselCardComponent;
